// src/stores/useAuthorStore.js
import { defineStore } from "pinia";
import * as authorService from "@/services/authorService";

export const useAuthorStore = defineStore("author", {
  state: () => ({
    authors: [],
    selectedAuthorIds: [],
    selectedAuthor: null, // autor detalhado (modal)
    idAuthor: null,
    error: null,
    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,
    searchTerm: "",
    shouldForcePageOne: false,
  }),

  getters: {
    isAllSelected: (state) => (total) => {
      return state.selectedAuthorIds.length === total;
    },
  },

  actions: {
    setIdAuthor(id) {
      this.idAuthor = id;
    },

    toggleSelection(id) {
      const index = this.selectedAuthorIds.indexOf(id);
      if (index > -1) {
        this.selectedAuthorIds.splice(index, 1);
      } else {
        this.selectedAuthorIds.push(id);
      }
    },

    selectAll(ids) {
      this.selectedAuthorIds = [...ids];
    },

    clearSelection() {
      this.selectedAuthorIds = [];
    },

    // limpa o autor selecionado (bom ao fechar modais)
    clearSelectedAuthor() {
      this.selectedAuthor = null;
    },

    async fetchAuthors() {
      try {
        const response = await authorService.fetchAuthors();
        this.authors = response.data;
      } catch (error) {
        this.error = error.response?.data || error.message;
        console.error("❌ Erro ao buscar autores:", this.error);
      }
    },

    async getAuthorById(id) {
      try {
        const response = await authorService.getAuthorById(id);
        this.selectedAuthor = response.data;
        return response.data;
      } catch (error) {
        const mensagem = error.response?.data || error.message;
        console.error("❌ Erro ao obter autor:", mensagem);
        throw new Error(mensagem);
      }
    },

    // 🔥 agora suporta allowDuplicateName (2º parâmetro)
    async createAuthor(data, allowDuplicateName = false, useElastic = false) {
      try {
        console.time("createAuthor");
        const response = await authorService.createAuthor(
          data,
          allowDuplicateName,
          useElastic,
        );
        console.timeEnd("createAuthor");

        this.shouldForcePageOne = true;

        await this.fetchAuthorsPaged(1, this.pageSize, this.searchTerm);
        return response.data;
      } catch (error) {
        console.error(
          "❌ Erro ao cadastrar autor na store:",
          error.response?.data || error.message || error,
        );
        throw error;
      }
    },

    // 🔥 idem para edição
    async updateAuthor(data, allowDuplicateName = false, useElastic = false) {
      try {
        await authorService.updateAuthor(data, allowDuplicateName, useElastic);

        // Atualiza na lista local se existir
        const index = this.authors.findIndex(
          (a) => a.authorId === data.authorId,
        );
        if (index !== -1) {
          this.authors[index] = { ...this.authors[index], ...data };
        }

        // Atualiza também o selectedAuthor se for o mesmo
        if (
          this.selectedAuthor &&
          this.selectedAuthor.authorId === data.authorId
        ) {
          this.selectedAuthor = { ...this.selectedAuthor, ...data };
        }
      } catch (error) {
        const mensagem = error.response?.data || error.message || error;
        console.error("❌ Erro ao atualizar autor:", mensagem);
        throw error;
      }
    },

    async deleteAuthorById(id, useElastic = false) {
      try {
        await authorService.deleteAuthor(id, useElastic);
        await this.fetchAuthorsPaged(
          this.currentPage,
          this.pageSize,
          this.searchTerm,
        );
        if (this.authors.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchAuthorsPaged(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
          );
        }
      } catch (error) {
        const mensagem = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir autor:", mensagem);
        throw new Error(mensagem);
      }
    },

    async deleteSelectedAuthors(useElastic = false) {
      if (!this.selectedAuthorIds.length) return;

      try {
        await authorService.deleteAuthorsBatch(
          this.selectedAuthorIds,
          useElastic,
        );
        this.clearSelection();
        await this.fetchAuthorsPaged(
          this.currentPage,
          this.pageSize,
          this.searchTerm,
        );
        if (this.authors.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchAuthorsPaged(
            this.currentPage,
            this.pageSize,
            this.searchTerm,
          );
        }
      } catch (error) {
        const mensagem = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir autores em lote:", mensagem);
        throw new Error(mensagem);
      }
    },

    async fetchAuthorsPaged(pageNumber, pageSize, search) {
      try {
        const response = await authorService.fetchAuthorsPaged(
          pageNumber,
          pageSize,
          search,
        );
        this.authors = response.data.data;
        this.totalCount = response.data.totalCount;
        this.totalPages = response.data.totalPages;
        this.currentPage = pageNumber;
      } catch (error) {
        const mensagem = error.response?.data || error.message;
        this.error = mensagem;
        console.error("❌ Erro ao buscar autores paginados:", mensagem);
        throw new Error(mensagem);
      }
    },

    setSearchTerm(term) {
      this.searchTerm = term;
      this.currentPage = 1;
      this.shouldForcePageOne = true;
      this.fetchAuthorsPaged(1, this.pageSize, term);
    },
  },
});
